import { useMemo, useState } from 'react';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { ConvertedRouteType } from '@lp/features/RoutePlanner/hooks/useConvertedRoute';
import { convertAccommodationLegs, convertRouteList, getRouteSegments } from '@lp/features/RoutePlanner/utils';
import { Case } from '@/api/apis/case';
import { HubRouteLocation, RouteTypeEnum } from '@/api/apis/route';
import { getTimeZonedDate, getTimeZonedDateString } from '@/utils/helpers';
import { OPERATION_TIME_BY_ORGAN } from '@/utils/route';
import { DataCacheType } from '@/queries/consts';
import { CompareTopBar, CompareViewTypeEnum } from './CompareTopBar';
import { CompareRouteListView } from './CompareRouteListView';
import { CompareRouteSchemaView } from './CompareRouteSchemaView';

type CompareModalBodyProps = {
  taskId: string;
  chainNumber: number;
  caseData: Case;
};

export const CompareModalBody = ({ taskId, chainNumber, caseData }: CompareModalBodyProps) => {
  const [viewType, setViewType] = useState<CompareViewTypeEnum>(CompareViewTypeEnum.RouteList);

  const {
    organType,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    donorHospitalDetails,
    txCenterDetails,
  } = caseData;
  const { legs, accommodationLegs, departureHub, arrivalHub } = useRouteDetails(RouteTypeEnum.Main);
  const { getLocationById } = useRouteLocations(DataCacheType.Snapshot);

  const donorHospitalDateTimeString = useMemo(() => {
    return getTimeZonedDateString(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  }, [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone]);

  const donorOperationEndDateTimeString = useMemo(() => {
    const dateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);

    if (!dateTime) return null;

    return dateTime.add({ milliseconds: OPERATION_TIME_BY_ORGAN[organType] }).toString();
  }, [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone, organType]);

  const route = useMemo<Pick<ConvertedRouteType, 'segments' | 'legs' | 'accommodationLegs'>>(() => {
    const defaultSegments = getRouteSegments({
      donorHospital: donorHospitalDetails!,
      txCenter: txCenterDetails,
      donorHospitalDateTimeString,
      donorOperationEndDateTimeString,
      legs,
      departureHub: getLocationById(departureHub?.locationId) as HubRouteLocation,
      arrivalHub: getLocationById(arrivalHub?.locationId) as HubRouteLocation,
    });

    const { segments: sortedSegments, legs: mainLegs } = convertRouteList(defaultSegments, legs);
    const routeData = convertAccommodationLegs(sortedSegments, accommodationLegs);

    return {
      segments: routeData.segments,
      legs: mainLegs,
      accommodationLegs: routeData.accommodationLegs,
    };
  }, [
    donorHospitalDateTimeString,
    donorOperationEndDateTimeString,
    donorHospitalDetails,
    txCenterDetails,
    legs,
    getLocationById,
    departureHub?.locationId,
    arrivalHub?.locationId,
    accommodationLegs,
  ]);

  return (
    <div className="border-t-1 border-base-60 py-4 flex flex-1 flex-col gap-4 overflow-hidden">
      <CompareTopBar caseData={caseData} viewType={viewType} onViewTypeChange={setViewType} />
      {viewType === CompareViewTypeEnum.RouteList && (
        <CompareRouteListView route={route} taskId={taskId} chainNumber={chainNumber} />
      )}
      {viewType === CompareViewTypeEnum.RouteSchema && (
        <CompareRouteSchemaView route={route} taskId={taskId} chainNumber={chainNumber} />
      )}
    </div>
  );
};
